import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import InputLabel from '@components/V4/Inputs/InputLabel';
import Select, { SelectOption } from '@components/V4/Select/Select';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { useDebounce } from '@hooks';
import { TriState } from '@shared/clientTypes/misc';
import AgoraSearchBar from '@utils/AgoraSearchbar';
import { Input } from 'antd';
import { useGetAllStudents } from 'apps/agora/src/actions';
import { useState } from 'react';

const GRADUATION_YEARS = Array.from({ length: 2040 - 2020 + 1 }, (_, index) =>
  (2020 + index).toString()
);

const RISK_OPTIONS: SelectOption[] = [
  { value: 'low', label: 'Low (under 30)' },
  { value: 'medium', label: 'Medium (30-59)' },
  { value: 'high', label: 'High (60+)' },
];

type Props = {
  search: string;
  studentId: string;
  graduationYears: string[];
  averageScoreLevel?: string;
  timeWithUsStart?: string;
  timeWithUsEnd?: string;
  userActiveStatus: TriState;
  resolvedStatus: TriState;
  onChange: (changes: Record<string, string | string[] | null>) => void;
};

const ChurnFilters = (props: Props) => {
  const {
    search,
    studentId,
    graduationYears,
    averageScoreLevel,
    timeWithUsStart,
    timeWithUsEnd,
    userActiveStatus,
    resolvedStatus,
    onChange,
  } = props;

  const [studentSearch, setStudentSearch] = useState('');
  const debouncedStudentSearch = useDebounce(studentSearch, 500);

  const { data: students, isLoading: isStudentsLoading } = useGetAllStudents({
    search: debouncedStudentSearch,
    limit: 20,
  });

  const studentOptions: SelectOption[] = [
    ...(debouncedStudentSearch ? [] : [{ label: 'ALL', value: null }]),
    ...(students?.map((student) => ({ label: student.fullName, value: student._id })) ?? []),
  ];

  const timeWithUsChange = (key: 'timeWithUsStart' | 'timeWithUsEnd', raw: string) =>
    onChange({ [key]: raw ? String(parseInt(raw, 10)) : null, page: '1' });

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full tablet:w-96">
        <AgoraSearchBar
          search={search}
          handleSearch={(value) => onChange({ search: value || null, page: '1' })}
          placeholder="Search by student name or email..."
        />
      </div>

      <div className="flex flex-wrap gap-4">
        <Select
          className="!w-48"
          label="Student"
          value={studentId || null}
          filterOption={() => true}
          searchValue={studentSearch}
          onSearch={setStudentSearch}
          onBlur={() => setStudentSearch('')}
          onSelect={(value) => onChange({ studentId: value ? String(value) : null, page: '1' })}
          options={studentOptions}
          notFoundContent={
            isStudentsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
          }
          placeholder="Search Students"
          variant="secondary"
        />

        <Select
          className="max-w-64"
          variant="secondary"
          label="Graduation Year"
          mode="multiple"
          value={graduationYears}
          onSelect={(value) =>
            typeof value === 'string' &&
            onChange({ graduationYears: [...graduationYears, value].join(','), page: '1' })
          }
          onDeselect={(value: string) =>
            onChange({
              graduationYears: graduationYears.filter((year) => year !== value).join(',') || null,
              page: '1',
            })
          }
          onClear={() => onChange({ graduationYears: null, page: '1' })}
          options={GRADUATION_YEARS.map((year) => ({ value: year, label: year }))}
        />

        <Select
          className="max-w-64"
          variant="secondary"
          label="Churn risk"
          value={averageScoreLevel || null}
          onSelect={(value) =>
            onChange({ averageScoreLevel: value ? String(value) : null, page: '1' })
          }
          onClear={() => onChange({ averageScoreLevel: null, page: '1' })}
          allowClear
          options={RISK_OPTIONS}
        />
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <InputLabel
            label="Time with us (days)"
            isRequired={false}
            isTouched={false}
            isValid={true}
            className="text-white text-xsm"
          />
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              min={0}
              value={timeWithUsStart ?? ''}
              onChange={(event) => timeWithUsChange('timeWithUsStart', event.target.value)}
              className="w-28"
            />
            <span>–</span>
            <Input
              type="number"
              placeholder="Max"
              min={0}
              value={timeWithUsEnd ?? ''}
              onChange={(event) => timeWithUsChange('timeWithUsEnd', event.target.value)}
              className="w-28"
            />
          </div>
        </div>

        <TriStateToggle
          title="Show Users:"
          options={[
            { label: 'Active', value: 'unchecked' },
            { label: 'Deactivated', value: 'checked' },
            { label: 'All', value: 'indeterminate' },
          ]}
          value={userActiveStatus}
          onChange={(value) => onChange({ userActiveStatus: value, page: '1' })}
        />

        <TriStateToggle
          title="Show Resolved:"
          options={[
            { label: 'Resolved', value: 'unchecked' },
            { label: 'Unresolved', value: 'checked' },
            { label: 'All', value: 'indeterminate' },
          ]}
          value={resolvedStatus}
          onChange={(value) => onChange({ resolvedStatus: value, page: '1' })}
        />
      </div>
    </div>
  );
};

export default ChurnFilters;
